import Swiper from "swiper";

/**
 * Wrapper class for Swiper slider implementation
 * @class Slider
 */
class Slider {
  /**
   * Creates a new slider instance
   * @param {string} selector - CSS selector for the slider container
   * @param {Object} options - Swiper options object
   * @param {number} [options.slidesPerView=1] - Number of slides to show
   * @param {number} [options.spaceBetween=20] - Space between slides in pixels
   */
  constructor(selector, options = {}) {
    if (!selector || typeof selector !== "string") {
      throw new Error("A valid selector string is required");
    }

    const defaultOptions = {
      slidesPerView: 1,
      spaceBetween: 20,
      ...options,
    };

    this.initSlider(selector, defaultOptions);
  }

  /**
   * Initializes the Swiper slider
   * @private
   * @param {string} selector - CSS selector
   * @param {Object} options - Swiper options
   */
  initSlider(selector, options) {
    if (!document.querySelector(selector)) {
      console.error(`Slider container not found: "${selector}"`);
      return;
    }

    try {
      this.slider = new Swiper(selector, options);
      this.setupResizeObserver();
    } catch (error) {
      console.error("Swiper initialization failed:", error);
    }
  }

  /**
   * Sets up the ResizeObserver for the slider
   * @private
   */
  setupResizeObserver() {
    if (!this.slider || !this.slider.el) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.slider.update();
    });

    this.resizeObserver.observe(this.slider.el);
  }

  /**
   * Gets the underlying Swiper instance
   * @returns {Swiper|null} The Swiper instance or null if not initialized
   */
  getInstance() {
    return this.slider;
  }

  /**
   * Enables keyboard navigation
   * @returns {Slider} The Slider instance for chaining
   */
  enableKeyboard() {
    if (this.slider) {
      this.slider.keyboard.enable();
    }
    return this;
  }

  /**
   * Destroys the slider instance and cleanup
   */
  destroy() {
    if (this.slider) {
      this.slider.destroy(true, true);
      this.slider = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

export default Slider;
